/**
 * ProbeFormDialog — modal pro přidání / úpravu sondy přístroje.
 *
 * Sonda = měřicí kanál na přístroji s vlastní metodikou + rozsahem +
 * jednotkou + typem. Sonda určuje metodiku.
 *
 * Inline-create pro `units` a `probeTypes` — admin může za chodu vytvořit
 * chybějící master data bez opouštění formuláře.
 */
import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { toastApiError } from '@/lib/errors';

const PROBE_READS = [
  { value: 'digital', label: 'Digitální' },
  { value: 'analog', label: 'Analogový' },
];

function FieldLabel({ children, required }) {
  return (
    <div className="ct-eyebrow mb-1">
      {children}
      {required && <span style={{ color: 'var(--ct-error)' }}> *</span>}
    </div>
  );
}

function NativeSelect({ value, onChange, options, placeholder, disabled }) {
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full px-3 py-2 rounded text-sm"
      style={{
        background: 'var(--ct-surface)',
        border: '1px solid var(--ct-border)',
        color: 'var(--ct-text)',
      }}
    >
      <option value="">{placeholder || '— vyberte —'}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

/**
 * Nested dialog pro inline create unitu nebo probe typu.
 * `kind` = 'unit' | 'probeType', `onCreated(newId)` = callback po úspěchu.
 *
 * Pokud je v parent formuláři vybraná metodika, předáme její
 * `laboratory` přes prop `inheritedLaboratory` — slouží jako quantity pro
 * unit a laboratory pro probeType (povinné, BE jinak vrátí 400). Bez vybrané
 * metodiky se inline create zablokuje s instrukcí.
 */
function InlineCreateDialog({ open, onClose, kind, onCreated, inheritedLaboratory }) {
  const [code, setCode] = useState('');
  const [labelOrSymbol, setLabelOrSymbol] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setCode(''); setLabelOrSymbol(''); setDescription(''); setSaving(false);
    }
  }, [open]);

  const handleCreate = async () => {
    if (!code.trim() || !labelOrSymbol.trim()) {
      toast.error('Vyplňte kód a symbol/název');
      return;
    }
    if (!inheritedLaboratory) {
      toast.error('Nejdřív vyberte metodiku — laboratoř/veličina se odvodí z ní');
      return;
    }
    setSaving(true);
    try {
      const endpoint = kind === 'unit' ? '/units' : '/probe-types';
      // Inline create necílí na pokročilou konfiguraci — používá rozumné defaulty:
      // - unit: baseUnit=false, factor=1, offset=0 (admin doladí v Nastavení laboratoře)
      // - probeType: jen základní pole + laboratory z metodiky
      const payload = kind === 'unit'
        ? {
            code: code.trim(),
            symbol: labelOrSymbol.trim(),
            description: description.trim(),
            quantity: inheritedLaboratory,
            baseUnit: false,
            conversionFactor: 1,
            conversionOffset: 0,
          }
        : {
            code: code.trim(),
            name: labelOrSymbol.trim(),
            description: description.trim(),
            laboratory: inheritedLaboratory,
          };
      const r = await api.post(endpoint, payload);
      onCreated(r.data.id);
      toast.success(kind === 'unit' ? 'Jednotka vytvořena' : 'Typ sondy vytvořen');
      onClose();
    } catch (err) {
      toastApiError(err, 'Nepodařilo se vytvořit záznam');
    } finally {
      setSaving(false);
    }
  };

  const titleText = kind === 'unit' ? 'Nová jednotka' : 'Nový typ sondy';
  const symbolLabel = kind === 'unit' ? 'Symbol (°C, bar, %)' : 'Název (Pt100, Termočlánek K)';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        style={{ background: 'var(--ct-surface)', border: '1px solid var(--ct-border)', maxWidth: '420px' }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: 'var(--ct-accent)' }}>{titleText}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {inheritedLaboratory ? (
            <p className="text-xs" style={{ color: 'var(--ct-text-disabled)' }}>
              {kind === 'unit' ? 'Veličina' : 'Laboratoř'}: <strong>{inheritedLaboratory}</strong> (z vybrané metodiky)
            </p>
          ) : (
            <p className="text-xs" style={{ color: 'var(--ct-warning)' }}>
              Pro inline založení musí být v hlavním formuláři vybraná metodika — určí laboratoř.
            </p>
          )}
          <div>
            <FieldLabel required>Kód (slug)</FieldLabel>
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder={kind === 'unit' ? 'degC' : 'pt100'} />
          </div>
          <div>
            <FieldLabel required>{symbolLabel}</FieldLabel>
            <Input value={labelOrSymbol} onChange={(e) => setLabelOrSymbol(e.target.value)} />
          </div>
          <div>
            <FieldLabel>Popis</FieldLabel>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose} className="ct-btn-secondary" disabled={saving}>Zrušit</Button>
          <Button
            onClick={handleCreate}
            className="ct-btn-primary"
            disabled={saving || !inheritedLaboratory}
          >
            {saving ? 'Ukládám…' : 'Vytvořit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ProbeFormDialog({ open, onClose, instrumentId, probe, onSaved }) {
  const isEdit = !!probe?.id;
  const [form, setForm] = useState({
    code: '', name: '', probeTypeId: '', probeRead: 'digital',
    unitId: '', rangeMin: '', rangeMax: '', methodologyId: '', notes: '',
  });
  const [units, setUnits] = useState([]);
  const [probeTypes, setProbeTypes] = useState([]);
  const [methodologies, setMethodologies] = useState([]);
  const [saving, setSaving] = useState(false);
  const [inlineCreate, setInlineCreate] = useState(null); // 'unit' | 'probeType' | null

  useEffect(() => {
    if (!open) return;
    // Init form
    if (probe) {
      setForm({
        code: probe.code || '',
        name: probe.name || '',
        probeTypeId: probe.probeTypeId || '',
        probeRead: probe.probeRead || 'digital',
        unitId: probe.unitId || '',
        rangeMin: probe.rangeMin ?? '',
        rangeMax: probe.rangeMax ?? '',
        methodologyId: probe.methodologyId || '',
        notes: probe.notes || '',
      });
    } else {
      setForm({
        code: '', name: '', probeTypeId: '', probeRead: 'digital',
        unitId: '', rangeMin: '', rangeMax: '', methodologyId: '', notes: '',
      });
    }
    // Načti master data
    Promise.all([
      api.get('/units').then(r => setUnits(r.data)).catch(() => setUnits([])),
      api.get('/probe-types').then(r => setProbeTypes(r.data)).catch(() => setProbeTypes([])),
      api.get('/methodologies').then(r => setMethodologies(r.data.filter(m => m.verified))).catch(() => setMethodologies([])),
    ]);
  }, [open, probe]);

  // Pokud je vybraná metodika, filtruj jednotky dle její laboratoře
  // (= units.quantity). Bez vybrané metodiky ukaž vše (uživatel nemá kontext).
  const selectedMethodology = useMemo(
    () => methodologies.find(m => m.id === form.methodologyId) || null,
    [methodologies, form.methodologyId]
  );
  const unitOptions = useMemo(() => {
    const filtered = selectedMethodology?.laboratory
      ? units.filter(u => !u.quantity || u.quantity === selectedMethodology.laboratory)
      : units;
    return filtered.map(u => ({ value: u.id, label: `${u.symbol} — ${u.description || u.code}` }));
  }, [units, selectedMethodology]);
  // Typy sond filtruj dle metodiky.laboratory (= probeType.laboratory).
  // Pre-migration typy bez laboratoře propustíme (defenzivní fallback).
  const probeTypeOptions = useMemo(() => {
    const filtered = selectedMethodology?.laboratory
      ? probeTypes.filter(pt => !pt.laboratory || pt.laboratory === selectedMethodology.laboratory)
      : probeTypes;
    return filtered.map(pt => ({ value: pt.id, label: pt.name }));
  }, [probeTypes, selectedMethodology]);
  const methodologyOptions = useMemo(
    () => methodologies
      .sort((a, b) => (a.code || '').localeCompare(b.code || '', 'cs'))
      .map(m => ({ value: m.id, label: `${m.code || ''} — ${m.title || ''} (${m.laboratory || ''})` })),
    [methodologies]
  );

  const handleSave = async () => {
    // Klientská validace povinných polí.
    const missing = [];
    if (!form.code.trim()) missing.push('Kód');
    if (!form.name.trim()) missing.push('Název');
    if (!form.probeTypeId) missing.push('Typ sondy');
    if (!form.unitId) missing.push('Jednotka');
    if (form.rangeMin === '' || form.rangeMax === '') missing.push('Rozsah');
    if (!form.methodologyId) missing.push('Metodika');
    if (missing.length) {
      toast.error(`Vyplňte: ${missing.join(', ')}`);
      return;
    }
    const rmin = parseFloat(form.rangeMin);
    const rmax = parseFloat(form.rangeMax);
    if (!Number.isFinite(rmin) || !Number.isFinite(rmax)) {
      toast.error('Rozsah musí být čísla');
      return;
    }
    if (rmin >= rmax) {
      toast.error('Rozsah "od" musí být menší než "do"');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        instrumentId,
        code: form.code.trim(),
        name: form.name.trim(),
        probeTypeId: form.probeTypeId,
        probeRead: form.probeRead,
        unitId: form.unitId,
        rangeMin: rmin,
        rangeMax: rmax,
        methodologyId: form.methodologyId,
        notes: form.notes.trim(),
      };
      if (isEdit) {
        await api.put(`/probes/${probe.id}`, payload);
        toast.success('Sonda upravena');
      } else {
        await api.post('/probes', payload);
        toast.success('Sonda přidána');
      }
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toastApiError(err, 'Nepodařilo se uložit sondu');
    } finally {
      setSaving(false);
    }
  };

  const handleInlineCreated = (kind, newId) => {
    if (kind === 'unit') {
      api.get('/units').then(r => {
        setUnits(r.data);
        setForm(f => ({ ...f, unitId: newId }));
      });
    } else if (kind === 'probeType') {
      api.get('/probe-types').then(r => {
        setProbeTypes(r.data);
        setForm(f => ({ ...f, probeTypeId: newId }));
      });
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent
          style={{ background: 'var(--ct-surface)', border: '1px solid var(--ct-border)', maxWidth: '640px' }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--ct-accent)' }}>
              {isEdit ? 'Upravit sondu' : 'Přidat sondu'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <FieldLabel required>Kód sondy</FieldLabel>
                <Input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="P1, T-temp, ..."
                  disabled={isEdit}
                  title={isEdit ? 'Kód nelze měnit po vytvoření' : undefined}
                />
              </div>
              <div>
                <FieldLabel required>Název</FieldLabel>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Hlavní sonda teploty"
                />
              </div>
            </div>

            <div>
              <FieldLabel required>Typ sondy</FieldLabel>
              <div className="flex gap-2">
                <div className="flex-1">
                  <NativeSelect
                    value={form.probeTypeId}
                    onChange={(v) => setForm({ ...form, probeTypeId: v })}
                    options={probeTypeOptions}
                    placeholder="— vyberte typ —"
                  />
                </div>
                <Button
                  type="button"
                  className="ct-btn-secondary text-xs"
                  onClick={() => setInlineCreate('probeType')}
                  title="Vytvořit nový typ sondy"
                >
                  <Plus className="w-3 h-3" /> Nový
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <FieldLabel required>Odečet</FieldLabel>
                <NativeSelect
                  value={form.probeRead}
                  onChange={(v) => setForm({ ...form, probeRead: v })}
                  options={PROBE_READS}
                  placeholder="— vyberte —"
                />
              </div>
              <div>
                <FieldLabel required>Jednotka</FieldLabel>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <NativeSelect
                      value={form.unitId}
                      onChange={(v) => setForm({ ...form, unitId: v })}
                      options={unitOptions}
                      placeholder="— vyberte jednotku —"
                    />
                  </div>
                  <Button
                    type="button"
                    className="ct-btn-secondary text-xs"
                    onClick={() => setInlineCreate('unit')}
                    title="Vytvořit novou jednotku"
                  >
                    <Plus className="w-3 h-3" /> Nová
                  </Button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <FieldLabel required>Rozsah od</FieldLabel>
                <Input
                  type="number"
                  step="any"
                  value={form.rangeMin}
                  onChange={(e) => setForm({ ...form, rangeMin: e.target.value })}
                />
              </div>
              <div>
                <FieldLabel required>Rozsah do</FieldLabel>
                <Input
                  type="number"
                  step="any"
                  value={form.rangeMax}
                  onChange={(e) => setForm({ ...form, rangeMax: e.target.value })}
                />
              </div>
            </div>

            <div>
              <FieldLabel required>Metodika</FieldLabel>
              <NativeSelect
                value={form.methodologyId}
                onChange={(v) => setForm({ ...form, methodologyId: v })}
                options={methodologyOptions}
                placeholder="— vyberte verifikovanou metodiku —"
              />
              <div className="text-xs mt-1" style={{ color: 'var(--ct-text-disabled)' }}>
                Metodika sondy určuje laboratoř i pravidla měření. Po startu kalibrace ji nelze měnit.
              </div>
            </div>

            <div>
              <FieldLabel>Poznámka</FieldLabel>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button onClick={onClose} className="ct-btn-secondary" disabled={saving}>Zrušit</Button>
            <Button onClick={handleSave} className="ct-btn-primary" disabled={saving}>
              {saving ? 'Ukládám…' : (isEdit ? 'Uložit změny' : 'Přidat sondu')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InlineCreateDialog
        open={inlineCreate === 'unit'}
        onClose={() => setInlineCreate(null)}
        kind="unit"
        onCreated={(id) => handleInlineCreated('unit', id)}
        inheritedLaboratory={selectedMethodology?.laboratory || null}
      />
      <InlineCreateDialog
        open={inlineCreate === 'probeType'}
        onClose={() => setInlineCreate(null)}
        kind="probeType"
        onCreated={(id) => handleInlineCreated('probeType', id)}
        inheritedLaboratory={selectedMethodology?.laboratory || null}
      />
    </>
  );
}
